import { useCallback, type ChangeEvent, type FocusEvent } from "react";
import classNames from "classnames";
import { OTPField as BaseOTPField } from "@base-ui/react/otp-field";
import { VERIFICATION_CODE_LENGTH } from "@constants";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import styles from "./OTPField.module.scss";
import type { OTPFieldProps } from "./OTPField.types";

/** True when focus is moving between two elements inside the group. */
const isFocusMovingWithin = (event: FocusEvent<HTMLElement>) =>
    event.relatedTarget instanceof Node &&
    event.currentTarget.contains(event.relatedTarget);

/**
 * One-time-code boxes on Base UI OTP Field, numeric only. Controlled and
 * library-agnostic: bind `value` / `onValueChange` from any form library.
 * `ref` points at the first slot; `onFocus` / `onBlur` fire for the group.
 */
export const OTPField = ({
    value,
    onValueChange,
    onValueComplete,
    onFocus,
    onBlur,
    name,
    length = VERIFICATION_CODE_LENGTH,
    autoFocus,
    disabled,
    readOnly,
    required,
    id,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    "aria-invalid": ariaInvalid,
    ref,
    className,
    style,
    "data-forced-state": forcedState,
}: OTPFieldProps) => {
    const assignFirstSlotRef = useCallback(
        (node: HTMLElement | null) => {
            mergeRefs([ref])(node instanceof HTMLInputElement ? node : null);
        },
        [ref],
    );
    const slotAriaProps = {
        "aria-describedby": ariaDescribedBy,
        ...(ariaInvalid ? { "aria-invalid": true } : undefined),
    };
    const slotIndexes = Array.from({ length }, (_, index) => index);
    const lastSlotIndex = length - 1;
    const activeSlotIndex = Math.min(value.length, lastSlotIndex);
    const forcedStateFor = (index: number) =>
        index === activeSlotIndex ? forcedState : undefined;

    // Base UI only completes when the value first reaches `length`, so typing
    // into the last slot of an already complete code completes again, letting
    // the user retype a wrong code and resubmit. Base UI writes the first
    // typed digit into the slot, and so does this.
    const completeAgainFromLastSlot = (
        event: ChangeEvent<HTMLInputElement>,
    ) => {
        const digit = event.currentTarget.value.replace(/\D/g, "").charAt(0);
        if (disabled || readOnly || value.length !== length || !digit) {
            return;
        }
        onValueComplete?.(value.slice(0, lastSlotIndex) + digit);
    };
    const slotChangeHandlerFor = (index: number) =>
        index === lastSlotIndex ? completeAgainFromLastSlot : undefined;

    return (
        <BaseOTPField.Root
            length={length}
            value={value}
            onValueChange={(nextValue) => onValueChange(nextValue)}
            onValueComplete={
                onValueComplete &&
                ((completeValue) => onValueComplete(completeValue))
            }
            validationType="numeric"
            name={name}
            id={id}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            className={classNames(styles.root, className)}
            style={style}
            onFocus={(event) => {
                if (!isFocusMovingWithin(event)) {
                    onFocus?.();
                }
            }}
            onBlur={(event) => {
                if (!isFocusMovingWithin(event)) {
                    onBlur?.();
                }
            }}
        >
            {slotIndexes.map((index) =>
                index === 0 ? (
                    <BaseOTPField.Input
                        key={index}
                        ref={assignFirstSlotRef}
                        className={styles.slot}
                        autoFocus={autoFocus}
                        aria-labelledby={ariaLabelledBy}
                        // Base UI 1.7.0 drops aria-label on the first slot, so it goes on the rendered input.
                        render={
                            ariaLabel
                                ? (props) => (
                                      <input
                                          {...props}
                                          aria-label={ariaLabel}
                                      />
                                  )
                                : undefined
                        }
                        onChange={slotChangeHandlerFor(index)}
                        data-forced-state={forcedStateFor(index)}
                        {...slotAriaProps}
                    />
                ) : (
                    <BaseOTPField.Input
                        key={index}
                        className={styles.slot}
                        aria-label={`Character ${index + 1} of ${length}`}
                        onChange={slotChangeHandlerFor(index)}
                        data-forced-state={forcedStateFor(index)}
                        {...slotAriaProps}
                    />
                ),
            )}
        </BaseOTPField.Root>
    );
};
